import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { selectCurrentToken, selectCustomerId } from '../auth/authSlice'; // Import selectors

// Define a service using a base URL and expected endpoints
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: 'https://app.hirebroker.co/api',
    // baseUrl: 'http://127.0.0.1:8000/api',
     // Use the full API URL for production
    prepareHeaders: (headers, { getState }) => {
      console.log('API Base URL:', 'https://app.hirebroker.co/api'); // Debugging line
      let token = null;
      if (typeof getState === 'function') {
        // Get token from Redux store
        token = selectCurrentToken(getState());
      } else {
        console.error("getState is not a function in prepareHeaders:", getState);
      }
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Property', 'User', 'Verification', 'PropertyNeed', 'Workspace', 'CoBroker', 'CrmLead', 'CrmReminder', 'Booking', 'Document', 'Notification', 'Subscription', 'Monetization', 'AgentProfile'],
  
  endpoints: (builder) => ({

    getProperties: builder.query({
      query: (params) => ({
        url: '/get_property', // Ensure this uses the relative path for proxy
        params, // This will include pagination parameters
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total_count: response.total || response.total_count || 0, // Use 'total' from the provided API response
      }),
      // For pagination, we want each page to be cached separately

      providesTags: (result) =>
        result?.data
          ? [
              // Map over the returned properties to create individual tags
              ...result.data.map(({ id }) => ({ type: 'Property', id })),
              // Also provide a general list tag for invalidation
              { type: 'Property', id: 'LIST' },
            ]
          : [{ type: 'Property', id: 'LIST' }],
    }),

    // Agent's own listings (JWT user; userid = agent customer id)
    getMyAgentProperties: builder.query({
      query: (params) => ({
        url: '/get_property',
        params: {
          limit: params?.limit || 50,
          offset: params?.offset || 0,
          userid: params?.userid,
        },
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total_count: response.total || response.total_count || (response.data?.length ?? 0),
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Property', id })),
              { type: 'Property', id: 'MY_LIST' },
            ]
          : [{ type: 'Property', id: 'MY_LIST' }],
    }),

    postAgentProperty: builder.mutation({
      query: (formData) => ({
        url: '/post_property',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: [{ type: 'Property', id: 'MY_LIST' }, { type: 'Property', id: 'LIST' }],
    }),

    deleteAgentProperty: builder.mutation({
      query: (id) => ({
        url: '/delete_property',
        method: 'POST',
        body: { id },
      }),
      invalidatesTags: [{ type: 'Property', id: 'MY_LIST' }, { type: 'Property', id: 'LIST' }],
    }),

    updatePropertyListing: builder.mutation({
      query: (body) => ({
        url: '/update_property_listing',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Property', id: 'MY_LIST' }, { type: 'Property', id: 'LIST' }],
    }),

    placesAutocomplete: builder.query({
      query: ({ input, country } = {}) => ({
        url: '/places/autocomplete',
        params: {
          input,
          ...(country ? { country } : {}),
        },
      }),
      transformResponse: (response) => response.predictions || [],
    }),

    placeDetails: builder.query({
      query: ({ place_id } = {}) => ({
        url: '/places/details',
        params: { place_id },
      }),
      transformResponse: (response) => response,
    }),

    getHostels: builder.query({
      query: (params) => ({
        url: '/get_hostels', // Ensure this uses the relative path for proxy
        params,
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total_count: response.total_count || 0,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Property', id })),
              { type: 'Property', id: 'HOSTEL_LIST' },
            ]
          : [{ type: 'Property', id: 'HOSTEL_LIST' }],
    }),

    getHotels: builder.query({
      query: (params) => ({
        url: '/get_hotels', // Ensure this uses the relative path for proxy
        params,
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total_count: response.total_count || 0,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Property', id })),
              { type: 'Property', id: 'HOTEL_LIST' },
            ]
          : [{ type: 'Property', id: 'HOTEL_LIST' }],
    }),


    // Add endpoint to get a single property by its slug_id
    getProperty: builder.query({
      query: (slug_id) => ({
        url: '/get_property', // Ensure this uses the relative path for proxy
        params: { slug_id }
      }),
      // The API returns an array with one item, so we extract it.
      transformResponse: (response) => (response.data && response.data.length > 0) ? response.data[0] : null,
      providesTags: (result, error, slug_id) => [{ type: 'Property', id: slug_id }],
    }),
    
    // Add endpoint to get slider images
    getSlider: builder.query({
      query: () => '/get_slider', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),
    // Add endpoint to get categories
    getCategories: builder.query({
      query: () => '/get_categories', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),

    // User signup/login (Firebase / social / phone)
    userSignup: builder.mutation({
      query: (userData) => ({
        url: '/user_signup', // This endpoint handles both signup and login
        method: 'POST',
        body: userData,
      }),
      transformResponse: (response, meta) => {
        // Portal separation may return 403 with { error: true, message }
        if (response?.error === true) {
          return response;
        }
        if (response.error === false && response.token && response.data?.id) {
          return {
            user: {
              ...response.data,
              account_role: response.data.account_role || 'customer',
              verification_status: response.data.verification_status || 'none',
            },
            token: response.token,
            customerId: response.data.id,
          };
        }
        return response;
      },
      transformErrorResponse: (response) => {
        return response?.data || { error: true, message: 'Authentication failed' };
      },
    }),

    // Email + password login (no Firebase required)
    passwordLogin: builder.mutation({
      query: (body) => ({
        url: '/user_login',
        method: 'POST',
        body,
      }),
      transformResponse: (response) => {
        if (response?.error === true) return response;
        if (response?.error === false && response.token && response.data?.id) {
          return {
            user: {
              ...response.data,
              account_role: response.data.account_role || 'customer',
              verification_status: response.data.verification_status || 'none',
            },
            token: response.token,
            customerId: response.data.id,
          };
        }
        return response;
      },
      transformErrorResponse: (response) =>
        response?.data || { error: true, message: 'Login failed' },
    }),

    // Email + password registration
    passwordRegister: builder.mutation({
      query: (body) => ({
        url: '/user_register_password',
        method: 'POST',
        body,
      }),
      transformResponse: (response) => {
        if (response?.error === true) return response;
        if (response?.error === false && response.token && response.data?.id) {
          return {
            user: {
              ...response.data,
              account_role: response.data.account_role || 'customer',
              verification_status: response.data.verification_status || 'none',
            },
            token: response.token,
            customerId: response.data.id,
          };
        }
        return response;
      },
      transformErrorResponse: (response) =>
        response?.data || { error: true, message: 'Registration failed' },
    }),

    // ✅ WORKING: get-user-data (different response format)
    getUserData: builder.query({ // Reverted to query based on server error
      query: () => '/get-user-data', // Reverted to GET request
      transformResponse: (response, meta) => {
        // This endpoint uses {"error":false,"data":{...}} format
        // Handle 404 gracefully
        if (meta?.response?.status === 404) {
          return null; // Return null for 404
        }
        return response.data || response;
      },
      providesTags: [{ type: 'User', id: 'CURRENT_USER' }], // Provide tag for caching
    }),

    // Update profile (location, contact, etc.) — Laravel update_profile
    updateUserProfile: builder.mutation({
      query: (userData, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId =
          selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        const body = new FormData();
        if (customerId) body.append('userid', customerId);
        Object.entries(userData || {}).forEach(([key, value]) => {
          if (key === 'userid') return;
          if (value !== undefined && value !== null) {
            body.append(key, value);
          }
        });
        return {
          url: '/update_profile',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [{ type: 'User', id: 'CURRENT_USER' }],
    }),

    // User dashboard endpoints
    // ✅ WORKING: getUserSavedProperties
    getUserSavedProperties: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        console.log('Fetching Saved Properties for Customer ID:', customerId); // Debugging line
        return {
          url: '/getUserSavedProperties', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: getUserSearches (with Bearer + customer_id)
    getUserSearches: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserSearches', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: getUserAlerts
    getUserAlerts: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserAlerts', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response, meta) => {
        // Handle 404 gracefully
        if (meta?.response?.status === 404) {
          return []; // Return empty array for 404
        }
        return response?.data || [];
      },
    }),

    // ✅ WORKING: getUserTours
    getUserTours: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserTours', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: getUserActivities
    getUserActivities: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserActivities', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // Add endpoint to get user's recently viewed properties
    getUserRecentlyViewed: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/getUserRecentlyViewed', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response) => response?.data || [],
    }),

    // ✅ WORKING: get_favourite_property (different response format)
    getUserFavouriteProperty: builder.query({
      query: (_, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/get_favourite_property', // Ensure this uses the relative path for proxy
          params: { customer_id: customerId }
        };
      },
      transformResponse: (response, meta) => {
        // This endpoint uses {"error":false,"message":"...","data": [...]}
        // Handle 404 gracefully
        if (meta?.response?.status === 404) {
          return []; // Return empty array for 404
        }
        return response?.data || [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Property', id })),
              { type: 'Property', id: 'FAVORITE_LIST' },
            ]
          : [{ type: 'Property', id: 'FAVORITE_LIST' }],
    }),

    // Add favorite property
    addFavorite: builder.mutation({
      query: (propertyId, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/add_favourite', // Ensure this uses the relative path for proxy
          method: 'POST',
          body: { // This is line 223
            customer_id: customerId,
            type: 1,
            property_id: propertyId
          },
        };
      },
      invalidatesTags: [{ type: 'Property', id: 'FAVORITE_LIST' }],
    }),

    // Delete favorite property
    deleteFavorite: builder.mutation({
      query: (propertyId, { getState } = {}) => {
        const safeGetState = typeof getState === 'function' ? getState : () => ({});
        const customerId = selectCustomerId(safeGetState()) || localStorage.getItem('customerId');
        return {
          url: '/delete_favourite', // Ensure this uses the relative path for proxy
          method: 'POST',
          body: { // This is line 241
            customer_id: customerId,
            type: 0,
            property_id: propertyId
          },
        };
      },
      invalidatesTags: [{ type: 'Property', id: 'FAVORITE_LIST' }],
    }),

    // Add endpoint to get count by cities and categories
    getCountByCitiesCategories: builder.query({
      query: () => '/get_count_by_cities_categoris', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),

    // Add endpoint to get facilities
    getFacilities: builder.query({
      query: () => '/get_facilities', // Ensure this uses the relative path for proxy
      transformResponse: (response) => response.data || [],
    }),

    // New Endpoints
    getArticles: builder.query({
      query: () => '/get_articles',
      transformResponse: (response) => response.data || [],
    }),
    getReportReasons: builder.query({
      query: () => '/get_report_reasons',
      transformResponse: (response) => response.data || [],
    }),
    getSeoSettings: builder.query({
      query: () => '/get_seo_settings',
      transformResponse: (response) => response.data || [],
    }),
    getSeoMarkets: builder.query({
      query: () => '/seo/markets',
      transformResponse: (response) => response.data || [],
    }),
    getSystemSettings: builder.query({
      query: () => ({
        url: '/get_system_settings',
        method: 'POST', // The user feedback indicates this is a POST request
      }),
      transformResponse: (response) => response.data || {},
    }),
    handlePaymentResponse: builder.query({ // Assuming this is a GET for now
      query: () => '/payment-response',
      transformResponse: (response) => response.data || response,
    }),
    getUserPersonalisedInterest: builder.query({
      query: () => '/personalised-fields',
      transformResponse: (response) => response.data || [],
    }),
    getInterestedUsers: builder.query({
      query: (params) => ({
        url: '/get_interested_users',
        params,
      }),
      transformResponse: (response) => response.data || [],
    }),
    getPropertyInquiry: builder.query({
      query: (params) => ({
        url: '/get_property_inquiry',
        params,
      }),
      transformResponse: (response) => response.data || [],
    }),
    getNotificationList: builder.query({
      query: () => '/get_notification_list',
      transformResponse: (response) => response.data || [],
      providesTags: ['Notification'],
    }),
    getNotifications: builder.query({
      query: (params = {}) => ({
        url: '/notifications',
        params: {
          limit: params.limit ?? 20,
          offset: params.offset ?? 0,
          unread_only: params.unread_only ? 1 : undefined,
        },
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total: response.total || 0,
        unread_count: response.unread_count || 0,
      }),
      providesTags: ['Notification'],
    }),
    getNotificationUnreadCount: builder.query({
      query: () => '/notifications/unread_count',
      transformResponse: (response) => ({
        unread_count: response.unread_count || 0,
      }),
      providesTags: ['Notification'],
    }),
    markNotificationsRead: builder.mutation({
      query: (body) => ({
        url: '/notifications/mark_read',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Notification'],
    }),
    markAllNotificationsRead: builder.mutation({
      query: () => ({
        url: '/notifications/mark_all_read',
        method: 'POST',
      }),
      invalidatesTags: ['Notification'],
    }),
    getPaymentDetails: builder.query({
      query: () => '/get_payment_details',
      transformResponse: (response) => response.data || [],
    }),
    getPaymentSettings: builder.query({
      query: () => '/get_payment_settings',
      transformResponse: (response) => response.data || [],
    }),
    getLimits: builder.query({
      query: () => '/get_limits',
      transformResponse: (response) => response.data || [],
    }),
    getMessages: builder.query({
      query: (params) => ({
        url: '/get_messages',
        params,
      }),
      transformResponse: (response) => response.data || [],
    }),
    getChats: builder.query({
      query: () => '/get_chats',
      transformResponse: (response) => response.data || [],
    }),
    getUserRecommendation: builder.query({
      query: () => '/get_user_recommendation',
      transformResponse: (response) => response.data || [],
    }),
    paypal: builder.query({ // Assuming this is a GET for now
      query: () => '/paypal',
      transformResponse: (response) => response.data || response,
    }),
    getAgentsDetails: builder.query({
      query: () => '/get_agents_details',
      transformResponse: (response) => response.data || [],
    }),

    // ——— Monetization (admin toggles) ———
    getMonetizationConfig: builder.query({
      query: () => '/monetization/config',
      transformResponse: (response) => response.data || response,
      providesTags: ['Monetization'],
    }),
    getSubscriptionPackages: builder.query({
      query: () => '/subscription/packages',
      transformResponse: (response) => ({
        packages: response.data || [],
        current: response.current || null,
        methods: response.methods || [],
      }),
      providesTags: ['Subscription'],
    }),
    startSubscriptionPayment: builder.mutation({
      query: (body) => ({
        url: '/subscription/start',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscription'],
    }),
    startSubscriptionTrial: builder.mutation({
      query: (body) => ({
        url: '/subscription/trial',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscription'],
    }),
    getSubscriptionPaymentStatus: builder.query({
      query: (tx_ref) => ({
        url: '/subscription/status',
        params: { tx_ref },
      }),
    }),
    getPlatformAds: builder.query({
      query: (params) => ({
        url: '/monetization/platform_ads',
        params,
      }),
      transformResponse: (response) => response.data || [],
    }),
    boostProperty: builder.mutation({
      query: (body) => ({
        url: '/monetization/boost_property',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Property', id: 'MY_LIST' }, { type: 'Property', id: 'LIST' }],
    }),
    startBoostPayment: builder.mutation({
      query: (body) => ({
        url: '/monetization/boost/start',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Property', id: 'MY_LIST' }, { type: 'Property', id: 'LIST' }],
    }),
    getBoostPaymentStatus: builder.query({
      query: (tx_ref) => ({
        url: '/monetization/boost/status',
        params: { tx_ref },
      }),
    }),
    startPurchasePayment: builder.mutation({
      query: (body) => ({
        url: '/monetization/purchase/start',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Monetization', { type: 'Property', id: 'MY_LIST' }, { type: 'Property', id: 'LIST' }],
    }),
    getPurchasePaymentStatus: builder.query({
      query: (tx_ref) => ({
        url: '/monetization/purchase/status',
        params: { tx_ref },
      }),
    }),
    getMonetizationMine: builder.query({
      query: () => '/monetization/mine',
      transformResponse: (response) => response.data || response,
      providesTags: ['Monetization'],
    }),
    getFeaturedAgents: builder.query({
      query: () => '/monetization/featured_agents',
      transformResponse: (response) => response.data || [],
    }),

    // ——— Trust: professional verification ———
    getVerificationStatus: builder.query({
      query: () => '/verification/status',
      transformResponse: (response) => response.data || response,
      providesTags: ['Verification'],
    }),
    submitVerification: builder.mutation({
      query: (formData) => ({
        url: '/verification/submit',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Verification', { type: 'User', id: 'CURRENT_USER' }],
    }),
    getVerifiedProfessionals: builder.query({
      query: (params) => ({
        url: '/verified_professionals',
        params,
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total: response.total || 0,
      }),
    }),
    getAgentProfile: builder.query({
      query: (id) => `/agent/${id}`,
      transformResponse: (response) => response.data || response,
      providesTags: (result, error, id) => [{ type: 'AgentProfile', id }],
    }),
    submitAgentReview: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/agent/${id}/reviews`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AgentProfile', id }],
    }),

    // ——— Collaboration: property needs notice board ———
    getPropertyNeeds: builder.query({
      query: (params) => ({
        url: '/property_needs',
        params,
      }),
      transformResponse: (response) => ({
        data: response.data || [],
        total: response.total || 0,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'PropertyNeed', id })),
              { type: 'PropertyNeed', id: 'LIST' },
            ]
          : [{ type: 'PropertyNeed', id: 'LIST' }],
    }),
    getPropertyNeed: builder.query({
      query: (id) => `/property_needs/${id}`,
      transformResponse: (response) => response.data || null,
      providesTags: (result, error, id) => [{ type: 'PropertyNeed', id }],
    }),
    createPropertyNeed: builder.mutation({
      query: (body) => ({
        url: '/property_needs',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'PropertyNeed', id: 'LIST' }, { type: 'PropertyNeed', id: 'MINE' }],
    }),
    updatePropertyNeed: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/property_needs/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'PropertyNeed', id },
        { type: 'PropertyNeed', id: 'LIST' },
        { type: 'PropertyNeed', id: 'MINE' },
      ],
    }),
    closePropertyNeed: builder.mutation({
      query: (id) => ({
        url: `/property_needs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'PropertyNeed', id: 'LIST' }, { type: 'PropertyNeed', id: 'MINE' }],
    }),
    getMyPropertyNeeds: builder.query({
      query: () => '/my_property_needs',
      transformResponse: (response) => response.data || [],
      providesTags: [{ type: 'PropertyNeed', id: 'MINE' }],
    }),
    respondToPropertyNeed: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/property_needs/${id}/respond`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'PropertyNeed', id },
        { type: 'PropertyNeed', id: 'LIST' },
        { type: 'PropertyNeed', id: 'MY_RESPONSES' },
      ],
    }),
    updatePropertyNeedResponseStatus: builder.mutation({
      query: ({ responseId, status }) => ({
        url: `/property_need_responses/${responseId}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: [{ type: 'PropertyNeed', id: 'LIST' }, { type: 'PropertyNeed', id: 'MINE' }],
    }),
    getMyPropertyNeedResponses: builder.query({
      query: () => '/my_property_need_responses',
      transformResponse: (response) => response.data || [],
      providesTags: [{ type: 'PropertyNeed', id: 'MY_RESPONSES' }],
    }),

    // ——— Workspace: co-broker, CRM, bookings, e-sign ———
    getWorkspaceDashboard: builder.query({
      query: () => '/workspace/dashboard',
      transformResponse: (response) => response.data || {},
      providesTags: ['Workspace'],
    }),
    getCoBrokerAgreements: builder.query({
      query: () => '/workspace/co_broker',
      transformResponse: (response) => response.data || [],
      providesTags: ['CoBroker'],
    }),
    createCoBrokerAgreement: builder.mutation({
      query: (body) => ({ url: '/workspace/co_broker', method: 'POST', body }),
      invalidatesTags: ['CoBroker', 'Workspace', 'Document'],
    }),
    respondCoBrokerAgreement: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/workspace/co_broker/${id}/respond`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CoBroker', 'Workspace', 'Document'],
    }),
    updateCoBrokerStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/workspace/co_broker/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['CoBroker', 'Workspace'],
    }),
    getCrmLeads: builder.query({
      query: (params) => ({ url: '/workspace/crm/leads', params }),
      transformResponse: (response) => response.data || [],
      providesTags: ['CrmLead'],
    }),
    createCrmLead: builder.mutation({
      query: (body) => ({ url: '/workspace/crm/leads', method: 'POST', body }),
      invalidatesTags: ['CrmLead', 'Workspace'],
    }),
    updateCrmLead: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/workspace/crm/leads/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CrmLead', 'Workspace'],
    }),
    deleteCrmLead: builder.mutation({
      query: (id) => ({ url: `/workspace/crm/leads/${id}`, method: 'DELETE' }),
      invalidatesTags: ['CrmLead', 'Workspace'],
    }),
    getCrmReminders: builder.query({
      query: (params) => ({ url: '/workspace/crm/reminders', params }),
      transformResponse: (response) => response.data || [],
      providesTags: ['CrmReminder'],
    }),
    createCrmReminder: builder.mutation({
      query: (body) => ({ url: '/workspace/crm/reminders', method: 'POST', body }),
      invalidatesTags: ['CrmReminder', 'CrmLead', 'Workspace'],
    }),
    updateCrmReminder: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/workspace/crm/reminders/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['CrmReminder', 'Workspace'],
    }),
    getWorkspaceAdvisors: builder.query({
      query: () => '/workspace/advisors',
      transformResponse: (response) => response.data || [],
    }),
    getAdvisorAvailability: builder.query({
      query: (params) => ({ url: '/workspace/availability', params }),
      transformResponse: (response) => response.data || [],
    }),
    saveAdvisorAvailability: builder.mutation({
      query: (body) => ({ url: '/workspace/availability', method: 'POST', body }),
    }),
    getWorkspaceBookings: builder.query({
      query: (params) => ({ url: '/workspace/bookings', params }),
      transformResponse: (response) => response.data || [],
      providesTags: ['Booking'],
    }),
    createWorkspaceBooking: builder.mutation({
      query: (body) => ({ url: '/workspace/bookings', method: 'POST', body }),
      invalidatesTags: ['Booking', 'Workspace'],
    }),
    updateBookingStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/workspace/bookings/${id}/status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Booking', 'Workspace'],
    }),
    getDocumentTemplates: builder.query({
      query: () => '/workspace/document_templates',
      transformResponse: (response) => response.data || [],
    }),
    getWorkspaceDocuments: builder.query({
      query: () => '/workspace/documents',
      transformResponse: (response) => response.data || { created: [], to_sign: [] },
      providesTags: ['Document'],
    }),
    createWorkspaceDocument: builder.mutation({
      query: (body) => ({ url: '/workspace/documents', method: 'POST', body }),
      invalidatesTags: ['Document', 'Workspace'],
    }),
    getWorkspaceDocument: builder.query({
      query: (id) => `/workspace/documents/${id}`,
      transformResponse: (response) => response.data || null,
      providesTags: (r, e, id) => [{ type: 'Document', id }],
    }),
    signWorkspaceDocument: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/workspace/documents/${id}/sign`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Document', 'Workspace'],
    }),
  }),
});

// Export auto-generated hooks for use in your components
export const {
  useGetPropertiesQuery,
  useGetMyAgentPropertiesQuery,
  usePostAgentPropertyMutation,
  useDeleteAgentPropertyMutation,
  useUpdatePropertyListingMutation,
  useLazyPlacesAutocompleteQuery,
  useLazyPlaceDetailsQuery,
  useGetPropertyQuery,
  useGetSliderQuery,
  useGetCategoriesQuery,
  useUserSignupMutation, // This mutation now handles both signup and login
  usePasswordLoginMutation,
  usePasswordRegisterMutation,
  useGetUserDataQuery,
  useGetUserSavedPropertiesQuery,
  useGetUserSearchesQuery,
  useGetUserAlertsQuery,
  useGetUserToursQuery,
  useGetUserActivitiesQuery,
  useGetUserFavouritePropertyQuery,
  useGetUserRecentlyViewedQuery,
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetHostelsQuery,
  useGetHotelsQuery,
  useGetCountByCitiesCategoriesQuery,
  useGetFacilitiesQuery,
  useUpdateUserProfileMutation, // Added new mutation export
  useGetArticlesQuery,
  useGetReportReasonsQuery,
  useGetSeoSettingsQuery,
  useGetSeoMarketsQuery,
  useGetSystemSettingsQuery,
  useHandlePaymentResponseQuery,
  useGetUserPersonalisedInterestQuery,
  useGetInterestedUsersQuery,
  useGetPropertyInquiryQuery,
  useGetNotificationListQuery,
  useGetNotificationsQuery,
  useGetNotificationUnreadCountQuery,
  useMarkNotificationsReadMutation,
  useMarkAllNotificationsReadMutation,
  useGetPaymentDetailsQuery,
  useGetPaymentSettingsQuery,
  useGetLimitsQuery,
  useGetMessagesQuery,
  useGetChatsQuery,
  useGetUserRecommendationQuery,
  usePaypalQuery,
  useGetAgentsDetailsQuery,
  useGetMonetizationConfigQuery,
  useGetSubscriptionPackagesQuery,
  useStartSubscriptionPaymentMutation,
  useStartSubscriptionTrialMutation,
  useLazyGetSubscriptionPaymentStatusQuery,
  useGetPlatformAdsQuery,
  useBoostPropertyMutation,
  useStartBoostPaymentMutation,
  useLazyGetBoostPaymentStatusQuery,
  useStartPurchasePaymentMutation,
  useLazyGetPurchasePaymentStatusQuery,
  useGetMonetizationMineQuery,
  useGetFeaturedAgentsQuery,
  useGetVerificationStatusQuery,
  useSubmitVerificationMutation,
  useGetVerifiedProfessionalsQuery,
  useGetAgentProfileQuery,
  useSubmitAgentReviewMutation,
  useGetPropertyNeedsQuery,
  useGetPropertyNeedQuery,
  useCreatePropertyNeedMutation,
  useUpdatePropertyNeedMutation,
  useClosePropertyNeedMutation,
  useGetMyPropertyNeedsQuery,
  useRespondToPropertyNeedMutation,
  useUpdatePropertyNeedResponseStatusMutation,
  useGetMyPropertyNeedResponsesQuery,
  useGetWorkspaceDashboardQuery,
  useGetCoBrokerAgreementsQuery,
  useCreateCoBrokerAgreementMutation,
  useRespondCoBrokerAgreementMutation,
  useUpdateCoBrokerStatusMutation,
  useGetCrmLeadsQuery,
  useCreateCrmLeadMutation,
  useUpdateCrmLeadMutation,
  useDeleteCrmLeadMutation,
  useGetCrmRemindersQuery,
  useCreateCrmReminderMutation,
  useUpdateCrmReminderMutation,
  useGetWorkspaceAdvisorsQuery,
  useGetAdvisorAvailabilityQuery,
  useSaveAdvisorAvailabilityMutation,
  useGetWorkspaceBookingsQuery,
  useCreateWorkspaceBookingMutation,
  useUpdateBookingStatusMutation,
  useGetDocumentTemplatesQuery,
  useGetWorkspaceDocumentsQuery,
  useCreateWorkspaceDocumentMutation,
  useGetWorkspaceDocumentQuery,
  useSignWorkspaceDocumentMutation,
} = apiSlice;
